import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import Dropzone from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import {
  Card, CardBody, Row, Col, Table, Button, Spinner,
  Modal, ModalHeader, ModalBody, Form, Label, Input,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from "../../../components/Common/PageHint";
import LangModal from "../../../components/Common/LangModal";
import { makeUploadParams } from "../../../helpers/imageResize";
import { compressPdf } from "../../../helpers/compressPdf";

const ODATA = "https://buharaapi.stechomeyazilim.info";
const IMG_BASE = "https://buharasubdomain.stechomeyazilim.info/";

const EMPTY = { title: "", active: true };

export default function CatalogPage() {
  document.title = "Kataloglar | BUHARA - Admin Paneli";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cover, setCover] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [langModal, setLangModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const getUploadParams = makeUploadParams("https://buharaadmin.stechomeyazilim.info:8099/uploadfile");

  const getUploadParamsPDF = async ({ file }) => {
    let toSend = file;
    try {
      const mb = (file.size / 1024 / 1024).toFixed(0);
      if (file.size > 20 * 1024 * 1024) {
        setPdfInfo(`PDF küçültülüyor (${mb} MB)… Lütfen bekleyin.`);
      }
      toSend = await compressPdf(file, {
        dpi: 150,
        quality: 0.75,
        skipBelowMB: 20,
        onProgress: (done, total) => setPdfInfo(`PDF küçültülüyor… sayfa ${done}/${total}`),
      });
      if (toSend !== file) {
        setPdfInfo(`Küçültme tamam. Yükleniyor…`);
      } else {
        setPdfInfo(null);
      }
    } catch (e) {
      console.error(e);
      setPdfInfo(null);
      toSend = file;
    }
    const body = new FormData();
    body.append("dataFile", toSend);
    return { url: "https://buharaadmin.stechomeyazilim.info:8099/uploadfile", body };
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ODATA}/Media`);
      const list = (res.data?.value || []).filter((m) => m.Media_IsCatalog === true);
      setRows(list);
    } catch (err) {
      console.error(err);
      toastr.error("Kataloglar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY);
    setCover(null);
    setPdf(null);
    setPdfInfo(null);
    setModal(true);
  };

  const openEdit = (row) => {
    setEditId(row.ID);
    setForm({ title: row.Media_Tile || "", active: row.Media_IsActive !== false });
    setCover(row.Media_ImageCover || null);
    setPdf(row.Media_Url || null);
    setPdfInfo(null);
    setModal(true);
  };

  const onCover = ({ xhr }) => {
    if (!xhr) return;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          setCover(JSON.parse(xhr.response).file.filename);
        } catch { /* ignore */ }
      }
    };
  };

  const onPdf = ({ xhr }, status) => {
    if (status === "done") setPdfInfo(null);
    if (!xhr) return;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          setPdf(JSON.parse(xhr.response).file.filename);
          setPdfInfo(null);
        } catch { /* ignore */ }
      }
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toastr.warning("Başlık giriniz.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        Media_Tile: form.title,
        Media_ImageCover: cover,
        Media_Url: pdf,
        Media_Type: pdf ? "application/pdf" : null,
        Media_IsActive: !!form.active,
        Media_IsCatalog: true,
      };
      if (editId) {
        await axios.patch(`${ODATA}/Media(${editId})`, payload);
        toastr.success("Katalog güncellendi.");
      } else {
        await axios.post(`${ODATA}/Media`, payload);
        toastr.success("Katalog eklendi.");
      }
      setModal(false);
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.Media_Tile}" katalogunu silmek istiyor musunuz?`)) return;
    try {
      await axios.delete(`${ODATA}/Media(${row.ID})`);
      toastr.success("Katalog silindi.");
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Silme sırasında hata oluştu.");
    }
  };

  const Preview = ({ meta }) => {
    const { name, percent, status } = meta;
    return (
      <div className="preview-box">
        <span className="name">{name}</span> -{" "}
        <span className="status">{status === "done" ? "Başarıyla Yüklendi" : "Yükleniyor"}</span>
        {status !== "done" && <span className="percent"> ({Math.round(percent)}%)</span>}
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Kataloglar" />
        <PageHint
          what="Ürün katalogları (PDF + kapak görseli) bu ekrandan yüklenir."
          where="Sitedeki E-Katalog sayfası: kataloglar kapak görseliyle listelenir, tıklanınca sayfa çevirmeli olarak açılır. Her dil için ayrı PDF, kapak ve başlık Çeviriler düğmesinden girilir."
          links={[{ label: "Kataloglar sayfası", path: "/kataloglar" }]}
        />

        <LangModal
          isOpen={langModal}
          toggle={() => setLangModal(false)}
          title="Katalog"
          tableName="Media_Lang"
          foreignKey="Media_ID"
          foreignKeyValue={selectedItem?.ID}
          sourceRecord={selectedItem}
          fields={[
            { name: "Media_ImageCover", label: "Kapak Görseli", type: "image" },
            { name: "Media_Url", label: "Katalog PDF", type: "file" },
            { name: "Media_Tile", label: "Başlık", type: "text" },
          ]}
        />

        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Kataloglar</h5>
                  <Button color="success" onClick={openAdd}>
                    <i className="bx bx-plus me-1" /> Yeni Katalog Ekle
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Kapak</th>
                          <th>Başlık</th>
                          <th>PDF</th>
                          <th>Durum</th>
                          <th className="text-end">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              Henüz katalog yok. Kapak görseli ve PDF yükleyerek ekleyin.
                            </td>
                          </tr>
                        ) : rows.map((row, i) => (
                          <tr key={row.ID}>
                            <td>{i + 1}</td>
                            <td>
                              {row.Media_ImageCover ? (
                                <img className="avatar-xl" src={IMG_BASE + row.Media_ImageCover} alt="" />
                              ) : "—"}
                            </td>
                            <td>{row.Media_Tile}</td>
                            <td>
                              {row.Media_Url ? (
                                <a href={IMG_BASE + row.Media_Url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                                  <i className="mdi mdi-file-pdf-box me-1" /> Görüntüle
                                </a>
                              ) : "—"}
                            </td>
                            <td>{row.Media_IsActive !== false ? "Aktif" : "Pasif"}</td>
                            <td className="text-end">
                              <Button color="link" className="text-success p-0 me-3" onClick={() => openEdit(row)}>
                                <i className="mdi mdi-pencil font-size-18" />
                              </Button>
                              <Button color="link" className="text-info p-0 me-3" onClick={() => { setSelectedItem(row); setLangModal(true); }}>
                                <i className="mdi mdi-translate font-size-18" />
                              </Button>
                              <Button color="link" className="text-danger p-0" onClick={() => handleDelete(row)}>
                                <i className="mdi mdi-delete font-size-18" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)} tag="h4">
            {editId ? "Katalog Düzenle" : "Katalog Ekle"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSave}>
              <div className="mb-3">
                <Label className="form-label">Başlık</Label>
                <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} required />
              </div>

              <div className="mb-3">
                <Label className="form-label">Kapak Görseli</Label>
                <Dropzone
                  getUploadParams={getUploadParams}
                  onChangeStatus={onCover}
                  maxFiles={1}
                  accept="image/*"
                  canRemove={false}
                  PreviewComponent={Preview}
                  styles={{ dropzone: { overflow: "auto", border: "1px solid #999", background: "#f5f5f5" } }}
                />
                {cover && (
                  <img className="avatar-xl mt-2" src={IMG_BASE + cover} alt="" />
                )}
              </div>

              <div className="mb-3">
                <Label className="form-label">Katalog PDF Dosyası</Label>
                <Dropzone
                  getUploadParams={getUploadParamsPDF}
                  onChangeStatus={onPdf}
                  maxFiles={1}
                  accept="application/pdf,.pdf"
                  canRemove={false}
                  PreviewComponent={Preview}
                  styles={{ dropzone: { overflow: "auto", border: "1px solid #999", background: "#f5f5f5" } }}
                />
                {pdfInfo && <div className="text-info mt-2" style={{ fontSize: 13 }}>{pdfInfo}</div>}
                {pdf && (
                  <a href={IMG_BASE + pdf} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger mt-2">
                    <i className="mdi mdi-file-pdf-box me-1" /> {pdf}
                  </a>
                )}
                <small className="text-muted d-block mt-1">
                  Büyük PDF&apos;ler yüklemeden önce tarayıcıda otomatik küçültülür.
                </small>
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="catalog-active"
                  checked={form.active}
                  onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                />
                <label className="form-check-label" htmlFor="catalog-active">Sitede görünsün</label>
              </div>

              <div className="text-end">
                <Button type="submit" color="success" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : null} Kaydet
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
